import { computed, reactive, ref } from 'vue'
import * as Sentry from '@sentry/browser'
import { useItemSearchApi } from './useItemSearchApi.js'
import { parseSearchConditions } from './searchConditions.js'
import { buildSearchItemBox } from './itemBoxUtils.js'

export const useItemSearch = () => {
  const { fetchItems } = useItemSearchApi()
  const conditions = reactive(parseSearchConditions(''))
  const items = ref([])
  const status = ref('idle')
  const pagination = reactive({
    currentPage: 1,
    totalPages: 1,
    totalCount: null
  })
  const error = ref(null)
  const loadingTotalCount = ref(false)

  const filterCount = computed(
    () =>
      conditions.selectedCategoryIds.length +
      conditions.selectedSceneIds.length +
      conditions.selectedReceivingKindIds.length +
      (conditions.lowerBudget !== null ? 1 : 0) +
      (conditions.higherBudget !== null ? 1 : 0)
  )
  const isLoadingTotalCount = computed(
    () => loadingTotalCount.value || pagination.totalCount === null
  )

  const search = async (pageNumber, loadTotalCount = false) => {
    items.value = []
    error.value = null
    status.value = 'loading'
    if (loadTotalCount) loadingTotalCount.value = true
    try {
      const result = await fetchItems(conditions, pageNumber)
      items.value = result.items
        .map(item => buildSearchItemBox(item, conditions))
        .filter(item => item !== null)
      Object.assign(pagination, result.pagination)
      conditions.currentPage = result.pagination.currentPage
      status.value = 'success'
      if (pageNumber === 1) {
        dataLayer.push({
          event: 'search',
          search_term: conditions.keyword,
          result_count: pagination.totalCount,
          ...(conditions.via && { via: conditions.via })
        })
        // リロードで二重計上されないよう、URL からも via を外す。
        if (new URLSearchParams(location.search).has('via')) {
          const url = new URL(location.href)
          url.searchParams.delete('via')
          history.replaceState(history.state, '', url)
        }
        // 絞り込みやソート変更による再検索には引き継がない。
        conditions.via = null
      }
    } catch (cause) {
      Sentry.captureException(cause)
      error.value = cause
      status.value = 'error'
    } finally {
      if (loadTotalCount) loadingTotalCount.value = false
    }
  }

  // URL に via がないときは defaultVia を使う
  // 初回表示は direct、戻る・進むは browser_history を渡す。
  const searchFromUrl = (searchString, defaultVia = null) => {
    const parsed = parseSearchConditions(searchString)
    Object.assign(conditions, parsed, { via: parsed.via || defaultVia })
    return search(conditions.currentPage, true)
  }

  const changeSort = (value, keyword) => {
    conditions.sortSelectedValue = value
    conditions.keyword = keyword
    return search(1)
  }

  const changeFilters = (filters, keyword) => {
    Object.assign(conditions, filters, { keyword })
    return search(1, true)
  }

  const changePage = page => search(page)

  return {
    conditions,
    items,
    status,
    pagination,
    error,
    filterCount,
    isLoadingTotalCount,
    searchFromUrl,
    changeSort,
    changeFilters,
    changePage
  }
}
